import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

/**
 * Catches uncaught render-time errors and shows a brand-consistent fallback
 * instead of a white screen. Logs to console; in prod you'd send to Sentry/etc.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "An unexpected error occurred.";
    return (
      <div data-testid="error-boundary" className="min-h-screen bg-[#0A140F] text-[#F8F7F4] flex items-center justify-center px-4 py-16">
        <div className="hard-border bg-[#0F1D17] border-[#8B2B3A] max-w-xl w-full p-8">
          <div className="flex items-center gap-2 text-[#8B2B3A]">
            <AlertTriangle className="w-5 h-5" />
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase">Application error</div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-4 leading-tight">Something broke. Sorry about that.</h1>
          <p className="mt-3 text-[#8A9992] leading-relaxed">
            We've logged the issue. You can reload the page or head back to the homepage. If this keeps happening, drop us a note at <a className="text-[#0E8B7E] hover:underline" href="mailto:Inquiries@Caresenta.com">Inquiries@Caresenta.com</a>.
          </p>
          <details className="mt-5 text-[12px] text-[#4A5A52]">
            <summary className="cursor-pointer font-mono tracking-wider uppercase">Technical details</summary>
            <pre className="mt-2 p-3 bg-[#0A140F] border border-[#2A3E37] overflow-x-auto whitespace-pre-wrap text-[#8A9992]">{message}</pre>
          </details>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
              className="bg-[#8B2B3A] text-[#F8F7F4] px-5 py-3 text-sm font-medium hover:bg-[#5A1C26] transition-colors"
            >
              Reload page
            </button>
            <Link
              to="/"
              onClick={this.reset}
              data-testid="error-home-link"
              className="border border-[#F8F7F4] text-[#F8F7F4] px-5 py-3 text-sm font-medium hover:bg-[#F8F7F4] hover:text-[#0A140F] transition-colors"
            >
              Back to homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
